/**
 * uP — Aperçu temps réel du flocage
 * =================================================================================================
 * Custom element `<up-perso-preview>`, rendu à l'intérieur de `<up-perso>` par
 * `snippets/product-info.liquid`.
 *
 * Ce fichier ne contient AUCUNE valeur de mise en page : position, taille, police, couleur et
 * casse sont pilotées par les custom properties de `assets/up-perso-preview.css`.
 *
 * Contraintes du thème Stretch prises en compte :
 *
 *  - `up_perso_module` est dans `block_types_allowing_partial_rerender`, et `<product-rerender>`
 *    fait un `replaceWith()` du bloc entier à chaque changement de variante. L'élément est donc
 *    détruit / recréé en permanence : toute l'init est dans `connectedCallback()`, et la valeur
 *    saisie est mémorisée pour être restaurée.
 *  - `<product-rerender>` restaure le focus (par id) mais pas la valeur : on restaure la valeur.
 *  - Le listener `input` est délégué sur `<up-perso>`, les inputs étant recréés à chaque rerender.
 *  - Guard de registration en bas de fichier : sans lui, un rechargement du module lèverait un
 *    `NotSupportedError`.
 *  - `disconnectedCallback()` coupe les listeners via AbortController. La classe `up-perso` du
 *    bundle, elle, ajoute un listener sur `document` sans jamais le retirer (bug préexistant dans
 *    `assets/main.min.js`, non corrigeable ici car le bundle est minifié et sans sources) —
 *    cet élément-ci ne reproduit pas ce comportement.
 */

/**
 * Copiée À L'IDENTIQUE depuis `verifyInputs()` dans `assets/main.min.js`.
 *
 * `verifyInputs()` ne tourne que sur `change`, alors que l'aperçu lit sur `input`. Sans cet
 * alignement, un emoji collé s'afficherait sur le mockup puis disparaîtrait au blur.
 * Ne pas réécrire cette regex : elle doit rester le miroir exact de celle du bundle.
 */
const EMOJI_SEQUENCE =
  /\p{RI}\p{RI}|\p{Emoji}(\p{EMod}|\uFE0F\u20E3?|[\u{E0020}-\u{E007E}]+\u{E007F})?(\u200D(\p{RI}\p{RI}|\p{Emoji}(\p{EMod}|\uFE0F\u20E3?|[\u{E0020}-\u{E007E}]+\u{E007F})?))*/gu;

/** Idem : `\p{Emoji}` matche aussi `#`, `*`, les chiffres, `©` et `®`, qui restent autorisés. */
const EMOJI_ALLOWED = /^[#*0-9©®]$/;

/** Applique exactement la même suppression que `verifyInputs()`, sans toucher à l'input. */
function stripEmoji(value) {
  const forbidden = value.match(EMOJI_SEQUENCE)?.filter((match) => !EMOJI_ALLOWED.test(match)) || [];

  let result = value;
  forbidden.forEach((match) => {
    result = result.replace(match, '');
  });

  return result;
}

class UpPersoPreview extends HTMLElement {
  /**
   * Valeurs saisies, conservées entre deux rerenders partiels.
   * Statique : le bloc est détruit/recréé, l'instance ne survit pas.
   */
  static #storedValues = new Map();

  /** Clé de persistance : le couple (formulaire produit, nom de property) est déjà unique. */
  static #keyFor(input) {
    return `${input.getAttribute('form') || ''}:${input.name}`;
  }

  #abortController = null;
  #resizeObserver = null;

  /** Id du formulaire produit auquel appartiennent nos champs — voir #applyVariantState. */
  #formId = '';

  /** Dernier verdict calculé pour NOTRE formulaire, réappliqué quand un autre formulaire bouge. */
  #actif = false;

  connectedCallback() {
    const host = this.closest('up-perso');
    if (!host) return;

    this.#abortController = new AbortController();

    // Le bloc est recréé à chaque changement de variante de notre formulaire : l'état rendu
    // côté serveur est donc toujours le bon au moment où l'on se connecte.
    this.#formId = host.querySelector('input[form]')?.getAttribute('form') || '';
    this.#actif = !host.hasAttribute('disabled');

    host.addEventListener('input', this.#onInput.bind(this), {
      signal: this.#abortController.signal,
    });

    // uP: les espaces de bord sont rognés quand le client quitte le champ. « ␣Yassine␣ »
    // partait tel quel dans la propriété de ligne, donc dans le fichier envoyé au floqueur,
    // où il décale le prénom sur la planche.
    //
    // `change` et non `input` : rogner à chaque frappe empêcherait de taper l'espace d'un
    // prénom composé, la saisie se bloquerait au milieu.
    host.addEventListener('change', this.#onChange.bind(this), {
      signal: this.#abortController.signal,
    });

    // Correction du verdict du bundle sur l'affichage du module — voir #applyVariantState.
    // L'ordre d'enregistrement est ce qui fait tout fonctionner : `up-perso` est notre
    // ancêtre, son `connectedCallback` s'exécute donc AVANT le nôtre, et son écouteur
    // `variant:change` est enregistré avant celui-ci. Sur `document`, les écouteurs sont
    // appelés dans l'ordre d'enregistrement : le nôtre passe en dernier et a le dernier mot.
    document.addEventListener('variant:change', (event) => this.#applyVariantState(host, event), {
      signal: this.#abortController.signal,
    });

    this.#restore(host);

    // La réduction se mesure en pixels : tant que la police de flocage n'est pas chargée,
    // la mesure porte sur la police de repli et le facteur serait faux.
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#refit();
    });

    // Le bloc est masqué (`display: none`) quand la variante n'a pas la personnalisation :
    // largeur nulle, donc mesure impossible. On refait le calcul dès qu'il reprend une taille.
    this.#resizeObserver = new ResizeObserver(() => this.#refit());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Réapplique l'affichage du module à partir de la valeur d'option configurée.
   *
   * POURQUOI CE CODE EXISTE — la classe `up-perso` de `assets/main.min.js` décide de
   * masquer ou non le module en testant une chaîne écrite en dur :
   *
   *     variant.options.includes("Avec personnalisation")
   *
   * Le bundle est minifié et livré sans sources : cette chaîne n'est pas modifiable.
   * Renommer la valeur d'option côté Shopify ferait donc disparaître le module en silence,
   * sans erreur, avec un supplément facturé et aucun champ pour saisir le prénom.
   *
   * On laisse le bundle faire son calcul, puis on écrase son résultat avec le bon, lu dans
   * `data-perso-option` (issu du métaobjet). Tant que la valeur reste « Avec
   * personnalisation », les deux verdicts coïncident et cette méthode ne change rien.
   *
   * `disabled` sur les inputs est le point critique : un champ désactivé n'est pas soumis,
   * donc la property `Personnalisation` disparaîtrait de la commande.
   */
  #applyVariantState(host, event) {
    const attendu = host.dataset.persoOption;
    if (!attendu) return;

    // `variant:change` remonte jusqu'à `document` depuis TOUS les formulaires produit de la
    // page (quick-buy des recommandations, featured-product…). Le bundle, lui, ne filtre pas :
    // un changement de variante ailleurs masquait notre module et désactivait nos champs, et
    // la commande partait sans la property `Personnalisation`. Pour un autre formulaire, on
    // ne recalcule rien : on réapplique notre dernier verdict, par-dessus celui du bundle.
    const formId = event.detail?.formId;
    if (!formId || !this.#formId || formId === this.#formId) {
      const variant = event.detail?.variant;
      this.#actif = Boolean(variant?.available && variant.options?.includes(attendu));
    }

    const actif = this.#actif;

    host.toggleAttribute('disabled', !actif);
    host.classList.toggle('hidden', !actif);
    host.querySelectorAll('input').forEach((input) => {
      input.disabled = !actif;
    });

    if (actif) this.#refit();
  }

  /** Apparie chaque ligne d'aperçu à son input, en repartant du DOM courant. */
  #pairs(host) {
    return Array.from(this.querySelectorAll('[data-perso-property]'))
      .map((line) => ({
        line,
        input: host.querySelector(`input[name="properties[${line.dataset.persoProperty}]"]`),
      }))
      .filter((pair) => pair.input !== null);
  }

  #restore(host) {
    this.#pairs(host).forEach(({ line, input }) => {
      const stored = UpPersoPreview.#storedValues.get(UpPersoPreview.#keyFor(input));

      if (stored && input.value === '') {
        input.value = input.maxLength > 0 ? stored.slice(0, input.maxLength) : stored;

        // `showRemainingChar()` de `<up-perso>` n'écoute que `keyup` : sans cet événement
        // synthétique, `.form-control__max-characters-count` resterait désynchronisé après
        // une écriture programmatique. On n'émet PAS `change`, qui déclencherait
        // `verifyInputs()` et donc un `reportValidity()` intempestif.
        input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
      }

      this.#paint(line, input.value);
    });
  }

  /**
   * Rogne les espaces de bord à la sortie du champ, puis relance l'aperçu par un `input`
   * synthétique — la ligne dessinée et la valeur mémorisée suivent ainsi le même chemin que
   * lors d'une frappe. Dispatcher `input` ne redéclenche pas `change` : pas de boucle.
   */
  #onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.name.startsWith('properties[')) return;

    // Espaces de bord retirés, et suites d'espaces internes ramenées à une seule. « Jean␣␣Pierre »
    // était refusé par le motif alors que l'intention est evidente : on corrige au lieu de
    // renvoyer une erreur. La tabulation et l'espace insécable, qu'un copier-coller amène
    // souvent, comptent comme des espaces — `\s` les couvre.
    // Les apostrophes typographiques deviennent l'apostrophe droite : c'est la SEULE que la
    // fonte de flocage possede. Les claviers de telephone et les traitements de texte posent
    // U+2019 d'office ; sans cette conversion le script de planche rejette le prenom.
    const rogne = input.value
      .replace(/[‘’ʼ‛´`]/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
    if (rogne === input.value) return;

    input.value = rogne;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  #onInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.name) return;

    const property = input.name.replace(/^properties\[|\]$/g, '');
    const line = this.querySelector(`[data-perso-property="${property}"]`);
    if (line === null) return;

    UpPersoPreview.#storedValues.set(UpPersoPreview.#keyFor(input), input.value);
    this.#paint(line, input.value);
  }

  /** La casse est gérée en CSS (`--up-perso-text-transform`), pas ici. */
  #paint(line, rawValue) {
    line.textContent = stripEmoji(rawValue).trim();
    this.#fit(line);
  }

  #refit() {
    this.querySelectorAll('[data-perso-line]').forEach((line) => this.#fit(line));
  }

  /**
   * Réduit proportionnellement une ligne trop large pour le mockup, comme le fait un
   * logiciel de découpe qui ajuste un texte dans un cadre.
   *
   * Aucune valeur de mise en page ici : la largeur maximale vient de
   * `--up-perso-line-N-max-width` dans le CSS. Elle est exprimée en cqw, c'est-à-dire en
   * pourcentage de la largeur du mockup — d'où le `parseFloat` puis la règle de trois.
   */
  #fit(line) {
    // Toujours repartir de la taille nominale, sinon on mesure une ligne déjà réduite.
    line.style.removeProperty('--up-perso-fit');

    const containerWidth = this.clientWidth;
    if (!line.textContent || !containerWidth) return;

    const maxCqw = parseFloat(
      getComputedStyle(line).getPropertyValue(
        `--up-perso-line-${line.dataset.persoLine}-max-width`
      )
    );
    if (!maxCqw) return;

    const available = (containerWidth * maxCqw) / 100;
    const natural = line.getBoundingClientRect().width;

    let facteur = natural > available ? available / natural : 1;

    // La HAUTEUR contraint aussi, depuis le passage a la zone 6 x 26 cm. Elle se mesure sur
    // l'ENCRE : un É deborde au-dessus de la capitale, un Ç sous la ligne de base. Le cadre
    // d'une ligne HTML ne dit rien de tout ca — il vaut l'interlignage, pas le trace. D'ou
    // le canvas, seul a exposer `actualBoundingBox*`, les vraies limites du dessin.
    const maxHCqw = parseFloat(
      getComputedStyle(line).getPropertyValue(
        `--up-perso-line-${line.dataset.persoLine}-max-height`
      )
    );

    if (maxHCqw) {
      const encre = this.#mesureEncre(line);
      if (encre) {
        const disponible = (containerWidth * maxHCqw) / 100;
        // `encre` est mesuree a la taille nominale : on compare avant reduction, puis on
        // garde le facteur le plus severe des deux.
        if (encre > disponible) facteur = Math.min(facteur, disponible / encre);
      }
    }

    if (facteur < 1) {
      line.style.setProperty('--up-perso-fit', facteur.toFixed(4));
    }
  }

  /**
   * Hauteur d'encre d'une ligne, en pixels, a sa taille nominale.
   *
   * `text-transform: uppercase` est applique par le CSS et le canvas ne le connait pas :
   * on passe donc le texte deja en capitales, sans quoi « rémi » se mesurerait en bas de
   * casse et l'accent ne serait pas au bon endroit.
   *
   * PRECISION — le canvas surestime de 0,7 a 1,1 % par rapport aux metriques exactes de la
   * fonte, quantification residuelle du navigateur. L'ecart va dans le bon sens : l'apercu
   * reduit un peu PLUS que la production, donc le client recoit au moins ce qu'il a vu. Au
   * pire 0,7 mm sur les 60 de la zone.
   *
   * Pour tomber a l'exact, il faudrait embarquer la table des yMin/yMax par glyphe, emise
   * par `build-flocage-font.py` (depot omerta-flocage) en meme temps que le WOFF2 — donc sans risque de
   * desynchronisation. Non fait : l'ecart actuel ne se voit pas a l'oeil.
   */
  #mesureEncre(line) {
    const cs = getComputedStyle(line);
    const texte = (line.textContent || '').toUpperCase();
    if (!texte.trim()) return 0;

    UpPersoPreview.pinceau ||= document.createElement('canvas').getContext('2d');
    const ctx = UpPersoPreview.pinceau;
    if (!ctx) return 0;

    // MESURE A GRANDE TAILLE, PUIS REGLE DE TROIS. Le canvas quantifie les metriques : a la
    // taille reelle (~39 px) il renvoyait 28 px ronds pour toutes les polices, soit 2,6 %
    // d'erreur — assez pour reduire « KARIM » a 97 % alors que la production le sort a 100 %.
    // A 1000 px la quantification tombe sous 0,1 %.
    const REF = 1000;
    ctx.font = `${REF}px ${cs.fontFamily}`;
    const m = ctx.measureText(texte);
    if (m.actualBoundingBoxAscent === undefined) return 0;

    const ratio = (m.actualBoundingBoxAscent + m.actualBoundingBoxDescent) / REF;
    return ratio * parseFloat(cs.fontSize);
  }
}

customElements.get('up-perso-preview') ||
  customElements.define('up-perso-preview', UpPersoPreview);

/**
 * Relais de l'interrupteur « Personnalise ton maillot » (`snippets/up-perso-toggle.liquid`).
 *
 * L'interrupteur remplace visuellement les deux boutons de variante Avec/Sans, qui restent
 * dans la page mais hors écran : ce sont eux qui portent le prix et que le thème pilote.
 * Un clic sur l'interrupteur coche donc le vrai bouton, et le thème fait le reste.
 *
 * Écouteur délégué posé une seule fois au chargement du module, et non dans un
 * `connectedCallback` : le bloc de personnalisation est détruit et recréé à chaque
 * changement de variante, alors que le sélecteur de variante, lui, reste en place.
 *
 * On cible le radio par sa VALEUR et non par son id : `option-value.liquid` construit un id
 * composite qu'il faudrait deviner, et qui casserait à la première mise à jour du thème.
 *
 * La recherche est limitée au formulaire de l'interrupteur (`data-perso-form`) : sans cela,
 * le premier radio de même valeur dans la page gagnait, celui d'un quick-buy par exemple.
 */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-perso-target]');
  if (!toggle) return;

  const formId = toggle.dataset.persoForm;
  const scope = formId ? `[form="${CSS.escape(formId)}"]` : '';
  const radio = document.querySelector(
    `input[type="radio"][value="${CSS.escape(toggle.dataset.persoTarget)}"]${scope}`
  );
  if (radio && !radio.checked) radio.click();
});
